import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPurchaseOrders, listPurchaseRequests, listGoodsReceipts } from '../../api/purchasing';
import { listSuppliers } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PrDetailDrawer } from '../../components/PrDetailDrawer';
import { PoDetailDrawer } from '../../components/PoDetailDrawer';
import { GrDetailDrawer } from '../../components/GrDetailDrawer';
import { ClickableRow, DashboardSkeleton, ErrorNote, HintNote, KPICard, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconClipboardCheck, IconFileText, IconTruck, IconUsers } from '../../components/icons';
import type { GoodsReceipt, PurchaseOrder, PurchaseRequest, Supplier } from '../../types/domain';

/**
 * Purchasing & Procurement workspace — real data only, same api/*
 * adapters as the vertical-slice pages. This is the dashboard a PURCHASING
 * role (or PREVIEW=Purchasing) should see instead of the generic
 * Executive Command Center: PR pipeline, PO status, GR receiving queue,
 * and suppliers, each with a real document-level drilldown link.
 */
export default function PurchasingDashboard() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [prs, setPrs] = useState<PurchaseRequest[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[] | null>(null);
  const [grs, setGrs] = useState<GoodsReceipt[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPr, setSelectedPr] = useState<PurchaseRequest | null>(null);
  const [selectedPo, setSelectedPo] = useState<PurchaseOrder | null>(null);
  const [selectedGr, setSelectedGr] = useState<GoodsReceipt | null>(null);

  const canReadPr = hasPermission('purchase_request', 'read');
  const canReadPo = hasPermission('purchase_order', 'read');
  const canReadGr = hasPermission('goods_receipt', 'read');
  const canReadSupplier = hasPermission('supplier', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadPr ? listPurchaseRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadPo ? listPurchaseOrders(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadGr ? listGoodsReceipts(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadSupplier ? listSuppliers() : Promise.resolve(null),
    ])
      .then(([prRows, poRows, grRows, supplierRows]) => {
        if (cancelled) return;
        setPrs(prRows);
        setPos(poRows);
        setGrs(grRows);
        setSuppliers(supplierRows);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadPr, canReadPo, canReadGr, canReadSupplier, activeBranchId]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const noAccess = !canReadPr && !canReadPo && !canReadGr && !canReadSupplier;
  const submittedPrs = prs ? prs.filter((pr) => pr.status === 'SUBMITTED') : [];
  const approvedPrs = prs ? prs.filter((pr) => pr.status === 'APPROVED') : [];
  const openPos = pos ? pos.filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED') : [];
  const draftGrs = grs ? grs.filter((gr) => gr.status === 'DRAFT') : [];

  return (
    <div>
      {noAccess && <ErrorNote text="Role Anda belum memiliki izin baca untuk modul manapun di Vertical-Slice-1 — hubungi TENANT_OWNER." />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 24 }}>
        {canReadPr && (
          <KPICard label="PR menunggu approval" value={submittedPrs.length} hint={`${prs?.length ?? 0} total PR`} icon={<IconFileText size={19} />} tone="warning" linkTo="/app/purchase-requests" />
        )}
        {canReadPr && (
          <KPICard label="PR siap dikonversi ke PO" value={approvedPrs.length} hint="Status APPROVED" icon={<IconFileText size={19} />} tone="success" linkTo="/app/purchase-requests" />
        )}
        {canReadPo && (
          <KPICard label="PO berjalan" value={openPos.length} hint={`${pos?.length ?? 0} total PO`} icon={<IconClipboardCheck size={19} />} tone="primary" linkTo="/app/purchase-orders" />
        )}
        {canReadGr && (
          <KPICard label="GR draft (belum posted)" value={draftGrs.length} hint={`${grs?.length ?? 0} total GR`} icon={<IconTruck size={19} />} tone="info" linkTo="/app/goods-receipts" />
        )}
        {canReadSupplier && <KPICard label="Suppliers" value={suppliers?.length ?? '—'} hint="Master pemasok" icon={<IconUsers size={19} />} tone="info" linkTo="/app/suppliers" />}
      </div>

      {canReadPr && approvedPrs.length === 0 && prs && prs.length > 0 && (
        <HintNote
          text="Tidak ada PR berstatus APPROVED yang siap dikonversi menjadi PO saat ini."
          action={
            <Link to="/app/purchase-requests" style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Lihat Purchase Requests →
            </Link>
          }
        />
      )}

      <div className="alex-grid-2col" style={{ marginBottom: 20 }}>
        {canReadPr && (
          <SectionCard title="Purchase Requests menunggu approval" action="/app/purchase-requests">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. PR</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Kebutuhan</th>
                </tr>
              </thead>
              <tbody>
                {submittedPrs.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={3}>
                      Tidak ada PR menunggu approval.
                    </td>
                  </tr>
                )}
                {submittedPrs.slice(0, 6).map((pr) => (
                  <ClickableRow key={pr.pr_id} onClick={() => setSelectedPr(pr)}>
                    <td style={tdStyle}>{pr.pr_number}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={pr.status} />
                    </td>
                    <td style={tdStyle}>{pr.needed_date ?? '—'}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}

        {canReadPo && (
          <SectionCard title="Purchase Orders berjalan" action="/app/purchase-orders">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. PO</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {openPos.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={2}>
                      Tidak ada PO berjalan.
                    </td>
                  </tr>
                )}
                {openPos.slice(0, 6).map((po) => (
                  <ClickableRow key={po.po_id} onClick={() => setSelectedPo(po)}>
                    <td style={tdStyle}>{po.po_number}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={po.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}
      </div>

      {canReadGr && grs && grs.length > 0 && (
        <SectionCard title="Goods Receipts terbaru" action="/app/goods-receipts">
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>No. GR</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {grs.slice(0, 6).map((gr) => (
                <ClickableRow key={gr.gr_id} onClick={() => setSelectedGr(gr)}>
                  <td style={tdStyle}>{gr.gr_number}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={gr.status} />
                  </td>
                </ClickableRow>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      <PrDetailDrawer pr={selectedPr} open={!!selectedPr} onClose={() => setSelectedPr(null)} />
      <PoDetailDrawer po={selectedPo} open={!!selectedPo} onClose={() => setSelectedPo(null)} />
      <GrDetailDrawer gr={selectedGr} open={!!selectedGr} onClose={() => setSelectedGr(null)} />
    </div>
  );
}
